import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import client, { resolveApiUrl } from "@/lib/api";
import ideaLogo from "@/assets/idea-logo.png";
import {
  User, Cpu, Gauge, Image as ImageIcon, Wrench, FileText,
  MemoryStick, HardDrive, Fan, Zap, Box, Snowflake, CircuitBoard, Cog,
  Clock, Calendar, Download, ExternalLink, Eye, ShieldCheck, Sparkles, Heart,
  Thermometer, Play,
} from "lucide-react";

/**
 * IDEA BUILD LAB — Final Build Report (v4 dark + electric-blue refinement).
 *
 * FRONTEND VISUAL ONLY. Zero backend edits. Reads the same
 * `/report/{token}` public DTO. Streaming, lifecycle, temperatures,
 * media and invoice APIs are untouched.
 */

// -----------------------------------------------------------------------
// Format helpers
// -----------------------------------------------------------------------
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
    }).toUpperCase();
  } catch { return "—"; }
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false }); }
  catch { return "—"; }
};
const fmtActive = (secs) => {
  if (secs === null || secs === undefined) return "—";
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  if (h <= 0) return `${m} Minute${m === 1 ? "" : "s"}`;
  if (m <= 0) return `${h} Hour${h === 1 ? "" : "s"}`;
  return `${h} Hour${h === 1 ? "" : "s"} ${m} Minute${m === 1 ? "" : "s"}`;
};

// -----------------------------------------------------------------------
// PC Specifications rows
// -----------------------------------------------------------------------
const SPEC_ROWS = [
  { key: "cpu",          label: "CPU",             icon: Cpu },
  { key: "gpu",          label: "GPU",             icon: CircuitBoard },
  { key: "motherboard",  label: "Motherboard",     icon: CircuitBoard },
  { key: "ram",          label: "RAM",             icon: MemoryStick, qty: "ram_qty" },
  { key: "storage",      label: "Storage",         icon: HardDrive,   qty: "storage_qty" },
  { key: "storage2",     label: "Second Storage",  icon: HardDrive,   qty: "storage2_qty" },
  { key: "power_supply", label: "Power Supply",    icon: Zap },
  { key: "case",         label: "Case",            icon: Box },
  { key: "cpu_cooler",   label: "CPU Cooler",      icon: Snowflake },
  { key: "fans",         label: "Fans",            icon: Fan,         qty: "fans_qty" },
];
function specDisplay(components) {
  if (!components || typeof components !== "object") return [];
  const rows = [];
  for (const r of SPEC_ROWS) {
    const v = components[r.key];
    if (typeof v !== "string" || !v.trim()) continue;
    const q = r.qty ? components[r.qty] : null;
    rows.push({ label: r.label, icon: r.icon, value: v.trim(),
      qty: typeof q === "number" && q > 1 ? q : null });
  }
  for (const o of (Array.isArray(components.others) ? components.others : [])) {
    if (!o || !o.name) continue;
    rows.push({ label: "Others", icon: Cog, value: String(o.name).trim(),
      qty: typeof o.qty === "number" && o.qty > 1 ? o.qty : null });
  }
  return rows;
}

const tempCell = (t) => {
  const raw = t && (typeof t === "object" ? t.value : t);
  const n = typeof raw === "number" ? raw : (raw != null ? Number(raw) : null);
  if (n === null || Number.isNaN(n)) return null;
  return Math.round(n);
};

// -----------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------
//
// Two call-sites:
//   • Customer:  <Route path="/report/:token" element={<CustomerBuildReport/>}>
//                → reads token from URL, fetches /api/report/{token}
//   • Supervisor: <BuildReportView data={preFetchedDto} …>
//                → uses the DTO already fetched by the supervisor page from
//                  the admin-gated /api/admin/build-reports/{order_id} route
//                  and skips the public /api/report/{token} fetch entirely.
// The public endpoint's token validation is untouched.
export default function CustomerBuildReport({ data: preloadedDto = null } = {}) {
  const params = useParams();
  const token = params?.token;
  const [dto, setDto] = useState(preloadedDto);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(!preloadedDto);
  const generatedAt = useMemo(() => new Date().toISOString(), []);

  useEffect(() => {
    // Supervisor path: DTO was passed in as a prop — render it directly and
    // do NOT hit /api/report/{token}. Keeps the public endpoint uninvolved
    // in the supervisor flow and avoids the "invalid_report_token" 404 that
    // resulted from `useParams().token` being undefined on the supervisor
    // route (/supervisor/build-reports/:orderId).
    if (preloadedDto) {
      setDto(preloadedDto);
      setErr(null);
      setLoading(false);
      return;
    }
    // Customer path: fetch by token from URL params (unchanged).
    if (!token) return;
    let alive = true; setLoading(true);
    client.get(`/report/${token}`)
      .then((r) => { if (alive) setDto(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || e?.message || "Report unavailable"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, preloadedDto]);

  if (loading) return <FallbackShell text="Loading report…" />;
  if (err) return <FallbackShell text={typeof err === "string" ? err : "Report unavailable."} title="Report Unavailable" />;
  if (!dto) return null;

  const specs   = specDisplay(dto.components || {});
  const cpuTemp = tempCell(dto.testing?.cpu_temp);
  const gpuTemp = tempCell(dto.testing?.gpu_temp);
  const startISO  = dto.timeline?.start_time;
  const finishISO = dto.timeline?.finish_time;
  const activeSec = dto.timeline?.active_build_seconds ?? null;
  const invoice   = dto.invoice || {};
  const invoiceUrl = invoice.url ? resolveApiUrl(invoice.url) : "";
  const invoiceDl  = invoice.download_url ? resolveApiUrl(invoice.download_url) : "";
  const isPdf = (invoice.content_type || "").toLowerCase().includes("pdf") || (invoice.filename || "").toLowerCase().endsWith(".pdf");
  const isImg = (invoice.content_type || "").toLowerCase().startsWith("image/") || /\.(png|jpe?g)$/i.test(invoice.filename || "");
  const front = (dto.photos || []).find((p) => p.angle === "front");
  const isVideoKind = dto.media_kind === "video" || !!dto.video;
  const pickupReady = (dto.pickup_status || "").toUpperCase() === "READY";

  return (
    <div className="idea-page-bg font-sans">
      <ReportStyle />
      <div className="print-only print-pagenum">PAGE 1 / 1 · Order #{dto.order_number || "—"}</div>

      <div className="a4-sheet" data-testid="report-sheet">

        {/* ============ HEADER ============ */}
        <header className="doc-header" data-testid="report-header">
          <div className="doc-header-grid">
            {/* Brand — original IDEA BUILD LAB logo asset (source of truth) */}
            <div className="doc-brand">
              <img
                src={ideaLogo}
                alt="IDEA BUILD LAB"
                className="doc-brand-logo"
                data-testid="report-brand-logo"
                draggable={false}
              />
            </div>
            {/* Title block */}
            <div className="doc-title-block">
              <div className="doc-title" data-testid="report-title">Final Build Report</div>
              <div className="doc-order-num" data-testid="report-order-number">Order #{dto.order_number || "—"}</div>
              <div className="doc-order-sub">Assembled &amp; verified by IDEA Build Lab</div>
            </div>
            {/* Meta */}
            <div className="doc-header-meta">
              <div className="doc-meta-line">
                <Calendar size={13} className="doc-meta-icn" />
                <div>
                  <div className="doc-meta-label">Build Date</div>
                  <div className="doc-meta-value" data-testid="report-build-date">{fmtDate(finishISO || startISO)}</div>
                </div>
              </div>
              <div className="doc-meta-line">
                <Clock size={13} className="doc-meta-icn" />
                <div>
                  <div className="doc-meta-label">Report Generated</div>
                  <div className="doc-meta-value">{fmtDate(generatedAt)}, {fmtTime(generatedAt)}</div>
                </div>
              </div>
            </div>
            {/* Status pill */}
            <div className="doc-status-pill" data-testid="report-status-badge">
              <div className="doc-status-dot" />
              <div>
                <div className="doc-status-primary">Build Completed</div>
                {pickupReady && <div className="doc-status-sub">Ready for Pickup</div>}
              </div>
            </div>
          </div>
          <div className="doc-rule-glow" />
        </header>

        {/* ============ ROW 1: 01 Customer + 02 Specs ============ */}
        <div className="doc-two-col">
          <Section n="01" title="Customer Information" Icon={User} testid="section-customer">
            <table className="doc-table" data-testid="customer-table">
              <tbody>
                <DocRow label="Customer Name"    value={dto.customer?.name || "—"} strong testid="customer-name" />
                <DocRow label="Phone / WhatsApp" value={dto.customer?.phone || dto.customer?.phone_masked || "—"} strong testid="customer-phone" />
                <DocRow label="Order Number"     value={`#${dto.order_number || "—"}`} strong testid="customer-order-number" />
              </tbody>
            </table>
          </Section>

          <Section n="02" title="PC Specifications" Icon={Cpu} testid="section-specs">
            {specs.length === 0 ? (
              <div className="doc-empty">No component specifications recorded.</div>
            ) : (
              <div className="doc-spec-list" data-testid="specs-table">
                {specs.map((s, i) => {
                  const Icn = s.icon || Cpu;
                  return (
                    <div key={`${s.label}-${i}`} className="doc-spec-row" data-testid={`spec-row-${i}`}>
                      <div className="doc-spec-icn"><Icn size={13} /></div>
                      <div className="doc-spec-label">{s.label}</div>
                      <div className="doc-spec-value">
                        {s.value}{s.qty ? <span className="doc-qty"> &nbsp;×{s.qty}</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>

        {/* ============ 03 TEMPERATURES ============ */}
        <Section n="03" title="Temperature Report" Icon={Gauge} testid="section-temps">
          <div className="doc-temp-grid">
            <TempCard label="CPU Temperature" Icon={Cpu}          value={cpuTemp} testid="temp-cpu" />
            <TempCard label="GPU Temperature" Icon={CircuitBoard} value={gpuTemp} testid="temp-gpu" />
          </div>
        </Section>

        {/* ============ 04 FINAL BUILD MEDIA ============ */}
        <Section n="04" title="Final Build Media" Icon={ImageIcon} testid="section-media">
          {isVideoKind ? (
            <div className="doc-media-grid">
              <MediaBlock title="Front Photo" testid="media-front">
                {front && front.url
                  ? <img src={resolveApiUrl(front.url)} alt="Front" data-testid="media-front-img" />
                  : <MediaEmpty text="Front photo unavailable" />}
              </MediaBlock>
              <MediaBlock title="Final Build Video" testid="media-video">
                {dto.video && dto.video.url ? (
                  <>
                    <video controls preload="metadata" playsInline data-testid="media-video-player" className="no-print">
                      {/*
                        For QuickTime (.mov) — Safari and Chromium both return
                        canPlayType('video/quicktime') = "" because the
                        container is codec-agnostic. That empty answer causes
                        <source type="video/quicktime"> to be discarded
                        without a fetch, so the <video> element ends up with
                        no source (0:00, no playback). Omit the `type` hint
                        for MOV so the browser sniffs the container/codec
                        itself. Other MIMEs keep their existing hint.
                      */}
                      {(() => {
                        const src = resolveApiUrl(dto.video.url);
                        const ct = (dto.video.content_type || "").toLowerCase();
                        const isMov = ct === "video/quicktime" || /\.mov(\?|$)/i.test(dto.video.filename || "") || /\.mov(\?|$)/i.test(dto.video.url || "");
                        return isMov
                          ? <source src={src} />
                          : <source src={src} type={dto.video.content_type || "video/mp4"} />;
                      })()}
                    </video>
                    <div className="print-only doc-media-empty">
                      <Play size={22} /><br />
                      FINAL BUILD VIDEO<br />
                      <span className="doc-media-file">{dto.video.filename || "video.mp4"}</span>
                    </div>
                  </>
                ) : <MediaEmpty text="Final build video unavailable" />}
              </MediaBlock>
            </div>
          ) : (
            <LegacyPhotoGallery photos={dto.photos || []} />
          )}
        </Section>

        {/* ============ 05 ASSEMBLY DETAILS ============ */}
        <Section n="05" title="Assembly Details" Icon={Wrench} testid="section-assembly">
          <table className="doc-table doc-table-two-col" data-testid="assembly-table">
            <tbody>
              <tr><th>Technician Name</th><td data-testid="asm-tech-name">{dto.technician?.name || "—"}</td>
                  <th>Technician ID</th><td data-testid="asm-tech-id">{dto.technician?.id || "—"}</td></tr>
              <tr><th>Station Number</th><td data-testid="asm-station">{dto.station?.id || "—"}</td>
                  <th>Build Date</th><td data-testid="asm-build-date">{fmtDate(startISO)}</td></tr>
              <tr><th>Start Time</th><td data-testid="asm-start">{fmtTime(startISO)}</td>
                  <th>Completion Time</th><td data-testid="asm-finish">{fmtTime(finishISO)}</td></tr>
            </tbody>
          </table>
          <div className="doc-total-time" data-testid="asm-total-time-row">
            <div className="doc-total-icn"><Clock size={18} /></div>
            <div className="doc-total-body">
              <div className="doc-total-label">Total Assembly Time</div>
              <div className="doc-total-value" data-testid="asm-total-time">{fmtActive(activeSec)}</div>
              <div className="doc-total-sub">Excludes pause time</div>
            </div>
          </div>
        </Section>

        {/* ============ 06 ORIGINAL INVOICE ============ */}
        <Section n="06" title="Original Invoice" Icon={FileText} testid="section-invoice">
          {!invoice.available ? (
            <div className="doc-invoice-empty" data-testid="invoice-none">
              <div className="doc-invoice-empty-label">Invoice</div>
              <div className="doc-invoice-empty-value">No Invoice Available</div>
            </div>
          ) : (
            <div className="doc-invoice-grid">
              <div className="doc-invoice-preview" data-testid="invoice-preview">
                {isPdf ? (
                  <object data={`${invoiceUrl}#toolbar=0`} type="application/pdf" aria-label="Invoice preview" className="no-print">
                    <div className="doc-invoice-fallback">PDF preview unavailable — use View or Download.</div>
                  </object>
                ) : isImg ? (
                  <img src={invoiceUrl} alt="Invoice" />
                ) : (
                  <div className="doc-invoice-fallback">Preview unavailable for this file type.</div>
                )}
                <div className="print-only doc-invoice-print-note">
                  ORIGINAL INVOICE<br />
                  <span className="doc-media-file">{invoice.filename || "invoice"}</span>
                </div>
              </div>
              <div className="doc-invoice-side">
                <div className="doc-invoice-meta">
                  <div><span className="doc-inv-meta-label">File</span><span className="doc-inv-meta-val" data-testid="invoice-filename">{invoice.filename || "invoice"}</span></div>
                  <div><span className="doc-inv-meta-label">Type</span><span className="doc-inv-meta-val" data-testid="invoice-type">{(invoice.content_type || "").split("/").pop() || "file"}</span></div>
                </div>
                <a href={invoiceUrl} target="_blank" rel="noreferrer" className="doc-btn doc-btn-primary no-print" data-testid="invoice-view">
                  <Eye size={13} /> <span>View Original Invoice</span>
                </a>
                <a href={invoiceDl} className="doc-btn doc-btn-ghost no-print" data-testid="invoice-download">
                  <Download size={13} /> <span>Download Original Invoice</span>
                </a>
                <a href={invoiceUrl} target="_blank" rel="noreferrer" className="doc-btn doc-btn-ghost no-print" data-testid="invoice-open">
                  <ExternalLink size={13} /> <span>Open in New Tab</span>
                </a>
              </div>
            </div>
          )}
        </Section>

        {/* ============ FOOTER ============ */}
        <div className="doc-rule-glow doc-rule-glow-thin" />
        <footer className="doc-footer" data-testid="report-footer">
          <div className="doc-footer-brand">
            <div className="doc-brand-mark small">IDE<span className="doc-brand-a">A</span></div>
            <div className="doc-brand-sub small">Build Lab</div>
          </div>
          <div className="doc-footer-col">
            <ShieldCheck size={14} className="doc-footer-icn" />
            <div>
              <div className="doc-footer-h">Assembled &amp; Verified</div>
              <div className="doc-footer-p">By IDEA Build Lab</div>
            </div>
          </div>
          <div className="doc-footer-col">
            <Sparkles size={14} className="doc-footer-icn" />
            <div>
              <div className="doc-footer-h">Quality Checked For</div>
              <div className="doc-footer-p">Stability and Performance</div>
            </div>
          </div>
          <div className="doc-footer-col">
            <Heart size={14} className="doc-footer-icn" />
            <div>
              <div className="doc-footer-h">Thank You</div>
              <div className="doc-footer-p">For Choosing IDEA</div>
            </div>
          </div>
          <div className="doc-footer-meta">
            <div>Order <b>#{dto.order_number || "—"}</b></div>
            <div className="doc-footer-p">Report Generated</div>
            <div>{fmtDate(generatedAt)}, {fmtTime(generatedAt)}</div>
            <div className="doc-footer-page">Page 1 / 1</div>
          </div>
        </footer>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------
function FallbackShell({ text, title }) {
  return (
    <div className="idea-page-bg min-h-screen flex flex-col items-center justify-center text-white/70 font-sans">
      <ReportStyle />
      <div className="text-[11px] tracking-[0.3em] uppercase mb-2" style={{ color: "#5a6b8a" }}>IDEA · Final Build Report</div>
      {title && <div className="text-lg font-semibold text-white">{title}</div>}
      <div className="text-sm mt-1">{text}</div>
    </div>
  );
}

function Section({ n, title, Icon, testid, children }) {
  return (
    <section className="doc-section" data-testid={testid}>
      <div className="doc-section-head">
        <span className="doc-section-num">{n}</span>
        {Icon && <Icon size={18} className="doc-section-icn" />}
        <span className="doc-section-title">{title}</span>
        <span className="doc-section-line" />
      </div>
      <div className="doc-section-body">
        <span className="doc-corner doc-corner-tl" />
        <span className="doc-corner doc-corner-br" />
        {children}
      </div>
    </section>
  );
}
function DocRow({ label, value, strong, testid }) {
  return (
    <tr data-testid={testid}>
      <th scope="row">{label}</th>
      <td className={strong ? "doc-strong" : ""}>{value}</td>
    </tr>
  );
}
function TempCard({ label, Icon, value, testid }) {
  return (
    <div className="doc-temp-card" data-testid={testid}>
      <span className="doc-corner doc-corner-tl" />
      <span className="doc-corner doc-corner-br" />
      <div className="doc-temp-icn"><Icon size={22} /></div>
      <div className="doc-temp-body">
        <div className="doc-temp-label">{label}</div>
        {value === null || value === undefined ? (
          <div className="doc-temp-empty" data-testid={`${testid}-empty`}>Not Recorded</div>
        ) : (
          <div className="doc-temp-value" data-testid={`${testid}-value`}>
            <span className="doc-temp-num">{value}</span><span className="doc-temp-unit">°C</span>
          </div>
        )}
      </div>
    </div>
  );
}
function MediaBlock({ title, testid, children }) {
  return (
    <div className="doc-media-block" data-testid={testid}>
      <div className="doc-media-label">{title}</div>
      <div className="doc-media-frame">
        <span className="doc-corner doc-corner-tl" />
        <span className="doc-corner doc-corner-br" />
        {children}
      </div>
    </div>
  );
}
function MediaEmpty({ text }) { return <div className="doc-media-empty">{text}</div>; }
function LegacyPhotoGallery({ photos }) {
  if (!photos.length) return <div className="doc-empty">Final build media unavailable for this build.</div>;
  return (
    <div className="doc-legacy-grid" data-testid="legacy-photos">
      {photos.map((p) => (
        <div key={p.angle} className="doc-legacy-cell" data-testid={`legacy-photo-${p.angle}`}>
          <img src={resolveApiUrl(p.url)} alt={p.label} />
          <div className="doc-legacy-label">{p.label}</div>
        </div>
      ))}
    </div>
  );
}

// Backward-compat named export (Supervisor's BuildReportViewPage).
export const BuildReportView = CustomerBuildReport;

// -----------------------------------------------------------------------
// STYLES — inline. Dark navy + electric-blue IDEA accents.
// -----------------------------------------------------------------------
function ReportStyle() {
  return (
    <style>{`
      :root {
        --paper: #05070d;
        --page-bg: #020307;
        --tint:  #0a0f1c;
        --tint-2:#0f1626;
        --ink:   #f4f6fb;
        --muted: #7d8aa8;
        --muted-2:#5a6b8a;
        --line:  #1c2438;
        --line-2:#243055;
        --accent:#1e90ff;
        --accent-2:#4bb0ff;
        --accent-soft: rgba(30,144,255,0.18);
      }
      .idea-page-bg { background: radial-gradient(1200px 900px at 50% -10%, #0a1224 0%, var(--page-bg) 60%); min-height: 100vh; }
      .font-sans { font-family: "Inter", "Helvetica Neue", Arial, sans-serif; }
      .print-only { display: none; }

      /* ---------- A4 sheet ---------- */
      .a4-sheet {
        background: linear-gradient(180deg, #060a14 0%, var(--paper) 60%);
        color: var(--ink);
        width: 210mm;
        min-height: 297mm;
        margin: 28px auto;
        padding: 18mm 18mm 14mm;
        position: relative;
        border: 1px solid var(--line-2);
        box-shadow: 0 0 0 1px rgba(30,144,255,0.05), 0 30px 80px rgba(0,0,0,0.7),
                    0 0 60px rgba(30,144,255,0.06) inset;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      @media (max-width: 900px) {
        .a4-sheet { width: 100%; min-height: 0; margin: 0; padding: 20px 16px;
                    border-left: 0; border-right: 0; box-shadow: none; }
      }

      /* ---------- Header ---------- */
      .doc-header { position: relative; }
      .doc-header-grid {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        grid-template-rows: auto auto;
        column-gap: 28px;
        row-gap: 14px;
        align-items: center;
      }
      .doc-brand { line-height: 1; grid-row: 1 / span 2; display: flex; align-items: center; }
      .doc-title-block { grid-column: 2; grid-row: 1; min-width: 0; }
      .doc-header-meta { grid-column: 2; grid-row: 2; display: grid; grid-auto-flow: column; column-gap: 22px; row-gap: 6px; align-items: center; }
      .doc-status-pill { grid-column: 3; grid-row: 1 / span 2; align-self: center; justify-self: end; }
      /* Original IDEA BUILD LAB logo asset — do not stretch. ~35% larger than the previous CSS wordmark. */
      .doc-brand-logo {
        height: 62px;
        width: auto;
        max-width: 240px;
        object-fit: contain;
        display: block;
        user-select: none;
        -webkit-user-drag: none;
      }
      /* Legacy small mark still used in the footer signature block */
      .doc-brand-mark {
        font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
        font-weight: 900;
        font-size: 42px;
        letter-spacing: -0.03em;
        color: var(--ink);
      }
      .doc-brand-mark.small { font-size: 22px; }
      .doc-brand-a {
        background: linear-gradient(180deg, var(--accent-2), var(--accent));
        -webkit-background-clip: text; background-clip: text; color: transparent;
        text-shadow: 0 0 24px rgba(30,144,255,0.4);
      }
      .doc-brand-sub {
        margin-top: 4px;
        font-size: 10px; font-weight: 700;
        letter-spacing: 0.32em; text-transform: uppercase; color: var(--muted);
      }
      .doc-brand-sub.small { font-size: 9px; margin-top: 2px; }
      .doc-title-block { min-width: 0; }
      .doc-title { font-size: 15px; font-weight: 800; letter-spacing: 0.02em; color: var(--accent-2); text-transform: uppercase; text-shadow: 0 0 14px rgba(30,144,255,0.42); white-space: nowrap; }
      .doc-order-num { color: var(--ink); font-size: 15px; font-weight: 800; letter-spacing: -0.01em; margin-top: 3px; }
      .doc-order-sub { color: var(--muted); font-size: 9.5px; letter-spacing: 0.22em; text-transform: uppercase; margin-top: 4px; font-weight: 600; }

      .doc-header-meta > .doc-meta-line { min-width: 0; }
      .doc-meta-line { display: grid; grid-template-columns: 18px 1fr; gap: 8px; align-items: center; }
      .doc-meta-icn { color: var(--accent); }
      .doc-meta-label { font-size: 9.5px; letter-spacing: 0.24em; text-transform: uppercase; color: var(--muted); font-weight: 700; }
      .doc-meta-value { font-size: 12px; color: var(--ink); font-weight: 700; letter-spacing: 0.04em; }

      .doc-status-pill {
        display: flex; align-items: center; gap: 10px;
        border: 1px solid var(--accent);
        padding: 10px 14px;
        background: rgba(30,144,255,0.08);
        box-shadow: 0 0 26px rgba(30,144,255,0.15), 0 0 0 1px rgba(30,144,255,0.15) inset;
        clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
      }
      .doc-status-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent-2); box-shadow: 0 0 12px var(--accent); }
      .doc-status-primary { color: var(--ink); font-size: 12px; font-weight: 800; letter-spacing: 0.2em; text-transform: uppercase; }
      .doc-status-sub { color: var(--muted); font-size: 9.5px; letter-spacing: 0.22em; text-transform: uppercase; margin-top: 2px; font-weight: 600; }

      .doc-rule-glow {
        height: 1px; margin: 18px 0 0;
        background: linear-gradient(90deg, transparent, var(--accent) 20%, var(--accent-2) 50%, var(--accent) 80%, transparent);
        box-shadow: 0 0 14px rgba(30,144,255,0.45);
      }
      .doc-rule-glow-thin { margin-top: 16px; opacity: 0.5; }

      /* ---------- Sections ---------- */
      .doc-two-col { display: grid; grid-template-columns: 1fr 1.35fr; gap: 16px; margin-top: 18px; }
      @media (max-width: 900px) { .doc-two-col { grid-template-columns: 1fr; } }

      .doc-section { margin-top: 18px; page-break-inside: avoid; break-inside: avoid; }
      .doc-section:first-child, .doc-two-col > .doc-section { margin-top: 0; }
      .doc-section-head { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
      .doc-section-num {
        display: inline-flex; align-items: center; justify-content: center;
        min-width: 34px; height: 22px; padding: 0 8px;
        font-size: 11px; letter-spacing: 0.18em; font-weight: 800; color: #ffffff;
        background: linear-gradient(180deg, var(--accent-2), var(--accent));
        clip-path: polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%);
        box-shadow: 0 0 14px rgba(30,144,255,0.35);
      }
      .doc-section-icn { color: var(--accent-2); }
      .doc-section-title { color: var(--ink); font-size: 13px; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 800; }
      .doc-section-line { flex: 1; height: 1px; background: linear-gradient(90deg, var(--line-2), transparent); }

      .doc-section-body {
        position: relative;
        background: linear-gradient(180deg, var(--tint-2), var(--tint));
        border: 1px solid var(--line-2);
        padding: 16px 18px;
        clip-path: polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px);
        transition: box-shadow 200ms ease, border-color 200ms ease;
      }
      .doc-section-body:hover { border-color: rgba(30,144,255,0.35); box-shadow: 0 0 24px rgba(30,144,255,0.06); }

      /* corner accents (rendered via absolutely positioned spans in JSX) */
      .doc-corner { position: absolute; width: 12px; height: 12px; pointer-events: none; }
      .doc-corner-tl { top: -1px; left: -1px; border-top: 1px solid var(--accent); border-left: 1px solid var(--accent); }
      .doc-corner-br { bottom: -1px; right: -1px; border-bottom: 1px solid var(--accent); border-right: 1px solid var(--accent); }

      /* ---------- Tables ---------- */
      .doc-table { width: 100%; border-collapse: collapse; }
      .doc-table th, .doc-table td {
        text-align: left; padding: 10px 12px; border-bottom: 1px solid var(--line);
        font-size: 12.5px; vertical-align: baseline;
      }
      .doc-table tr:last-child th, .doc-table tr:last-child td { border-bottom: 0; }
      .doc-table th { width: 42%; color: var(--muted); text-transform: uppercase; letter-spacing: 0.16em; font-size: 10.5px; font-weight: 700; }
      .doc-table td { color: var(--ink); font-weight: 600; }
      .doc-table td.doc-strong { color: var(--ink); font-weight: 700; }

      .doc-table-two-col th, .doc-table-two-col td { padding: 9px 12px; font-size: 12px; }
      .doc-table-two-col tr th:nth-child(3), .doc-table-two-col tr td:nth-child(4) { border-left: 1px solid var(--line); }

      /* ---------- Specs list ---------- */
      .doc-spec-list { display: grid; row-gap: 2px; }
      .doc-spec-row {
        display: grid; grid-template-columns: 24px 130px 1fr; gap: 10px; align-items: baseline;
        padding: 9px 6px; border-bottom: 1px solid var(--line);
      }
      .doc-spec-row:last-child { border-bottom: 0; }
      .doc-spec-icn { color: var(--accent-2); }
      .doc-spec-label { color: var(--muted); font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase; font-weight: 700; }
      .doc-spec-value { color: var(--ink); font-size: 12.5px; font-weight: 700; }
      .doc-qty { color: var(--accent-2); font-weight: 700; letter-spacing: 0.04em; }

      /* ---------- Temperature ---------- */
      .doc-temp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
      @media (max-width: 620px) { .doc-temp-grid { grid-template-columns: 1fr; } }
      .doc-temp-card {
        position: relative;
        background: linear-gradient(180deg, rgba(30,144,255,0.08), rgba(30,144,255,0.02));
        border: 1px solid var(--line-2);
        padding: 16px 18px;
        display: grid; grid-template-columns: 44px 1fr; gap: 14px; align-items: center;
        clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
        box-shadow: 0 0 22px rgba(30,144,255,0.06);
      }
      .doc-temp-icn {
        color: var(--accent-2); width: 44px; height: 44px;
        display: flex; align-items: center; justify-content: center;
        border: 1px solid var(--accent-soft); border-radius: 4px;
        background: rgba(30,144,255,0.06);
      }
      .doc-temp-label { color: var(--muted); font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 700; }
      .doc-temp-value { display: inline-flex; align-items: baseline; margin-top: 2px; }
      .doc-temp-num {
        font-size: 46px; font-weight: 800; line-height: 1; color: var(--accent-2);
        text-shadow: 0 0 22px rgba(30,144,255,0.35);
        font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
      }
      .doc-temp-unit { color: var(--accent); font-size: 16px; margin-left: 3px; font-weight: 700; letter-spacing: 0.02em; }
      .doc-temp-empty { color: var(--muted); font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 700; }

      /* ---------- Media ---------- */
      .doc-media-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
      @media (max-width: 900px) { .doc-media-grid { grid-template-columns: 1fr; } }
      .doc-media-block { min-width: 0; }
      .doc-media-label {
        color: var(--muted); font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase;
        font-weight: 700; margin-bottom: 8px;
      }
      .doc-media-frame {
        position: relative; overflow: hidden;
        border: 1px solid var(--line-2);
        background: #000;
        aspect-ratio: 16 / 10;
        transition: border-color 200ms ease, box-shadow 200ms ease;
      }
      .doc-media-frame:hover { border-color: var(--accent); box-shadow: 0 0 30px rgba(30,144,255,0.18); }
      .doc-media-frame img, .doc-media-frame video, .doc-media-frame object {
        width: 100%; height: 100%; object-fit: cover; display: block;
      }
      .doc-media-empty {
        position: absolute; inset: 0;
        display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 6px;
        color: var(--muted); font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase; text-align: center; padding: 12px;
      }
      .doc-media-file { text-transform: none; letter-spacing: 0.02em; font-size: 12px; color: var(--ink); }

      .doc-legacy-grid { display: grid; grid-template-columns: repeat(5, minmax(0,1fr)); gap: 10px; }
      @media (max-width: 820px) { .doc-legacy-grid { grid-template-columns: repeat(3, minmax(0,1fr)); } }
      @media (max-width: 520px) { .doc-legacy-grid { grid-template-columns: repeat(2, minmax(0,1fr)); } }
      .doc-legacy-cell { border: 1px solid var(--line-2); background: #000; }
      .doc-legacy-cell img { width: 100%; aspect-ratio: 1 / 1; object-fit: cover; display: block; }
      .doc-legacy-label { color: var(--muted); font-size: 9.5px; letter-spacing: 0.2em; text-transform: uppercase; padding: 6px 8px; border-top: 1px solid var(--line); }

      /* ---------- Assembly total time ---------- */
      .doc-total-time {
        margin-top: 14px;
        display: grid; grid-template-columns: 44px 1fr; gap: 14px; align-items: center;
        border: 1px solid var(--accent);
        padding: 14px 18px;
        background: rgba(30,144,255,0.08);
        clip-path: polygon(12px 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%, 0 12px);
        box-shadow: 0 0 26px rgba(30,144,255,0.18);
      }
      .doc-total-icn {
        width: 44px; height: 44px; display: flex; align-items: center; justify-content: center;
        color: var(--accent-2); border: 1px solid var(--accent-soft); border-radius: 4px; background: rgba(30,144,255,0.08);
      }
      .doc-total-label { color: var(--muted); font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 700; }
      .doc-total-value { color: var(--accent-2); font-size: 24px; font-weight: 800; margin-top: 2px;
        text-shadow: 0 0 20px rgba(30,144,255,0.35); }
      .doc-total-sub { color: var(--muted); font-size: 10.5px; letter-spacing: 0.06em; margin-top: 2px; }

      /* ---------- Invoice ---------- */
      .doc-invoice-grid { display: grid; grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); gap: 16px; align-items: start; }
      @media (max-width: 900px) { .doc-invoice-grid { grid-template-columns: 1fr; } }
      .doc-invoice-preview {
        position: relative; border: 1px solid var(--line-2); background: #000; min-height: 320px; overflow: hidden;
      }
      .doc-invoice-preview object, .doc-invoice-preview img {
        width: 100%; height: 480px; display: block; background: #fff; object-fit: contain;
      }
      .doc-invoice-fallback { padding: 40px 20px; text-align: center; color: var(--muted); font-size: 12px; }
      .doc-invoice-side { display: flex; flex-direction: column; gap: 10px; }
      .doc-invoice-meta {
        border: 1px solid var(--line-2); background: rgba(255,255,255,0.02); padding: 10px 12px;
        display: grid; row-gap: 6px;
      }
      .doc-invoice-meta > div { display: grid; grid-template-columns: 90px 1fr; gap: 8px; align-items: baseline; }
      .doc-inv-meta-label { color: var(--muted); font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; font-weight: 700; }
      .doc-inv-meta-val { color: var(--ink); font-size: 12.5px; font-weight: 700; word-break: break-all; }

      .doc-invoice-empty {
        border: 1px dashed var(--accent-soft); background: rgba(30,144,255,0.03);
        padding: 28px; text-align: center;
      }
      .doc-invoice-empty-label { color: var(--muted); font-size: 10px; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; }
      .doc-invoice-empty-value { color: var(--ink); font-size: 13px; letter-spacing: 0.18em; text-transform: uppercase; font-weight: 800; margin-top: 6px; }

      .doc-btn {
        display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        text-decoration: none; text-transform: uppercase; letter-spacing: 0.2em;
        font-size: 10.5px; font-weight: 800; padding: 11px 14px;
        border: 1px solid var(--accent); color: var(--ink); background: transparent;
        transition: background 200ms ease, box-shadow 200ms ease, transform 120ms ease;
      }
      .doc-btn:hover { background: rgba(30,144,255,0.12); box-shadow: 0 0 22px rgba(30,144,255,0.25); }
      .doc-btn:active { transform: translateY(1px); }
      .doc-btn-primary { background: linear-gradient(180deg, var(--accent-2), var(--accent)); color: #001125; border-color: var(--accent-2);
        box-shadow: 0 0 24px rgba(30,144,255,0.35); }
      .doc-btn-primary:hover { color: #001125; }
      .doc-btn-ghost { background: transparent; }

      /* ---------- Footer ---------- */
      .doc-footer {
        margin-top: 18px;
        display: grid; grid-template-columns: auto 1fr 1fr 1fr auto; gap: 16px; align-items: center;
        padding-top: 10px;
      }
      .doc-footer-brand { line-height: 1; }
      .doc-footer-col { display: grid; grid-template-columns: 18px 1fr; gap: 8px; align-items: center; border-left: 1px solid var(--line); padding-left: 14px; }
      .doc-footer-icn { color: var(--accent-2); }
      .doc-footer-h { color: var(--ink); font-size: 10px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 800; }
      .doc-footer-p { color: var(--muted); font-size: 10px; letter-spacing: 0.12em; margin-top: 2px; }
      .doc-footer-meta { text-align: right; border-left: 1px solid var(--line); padding-left: 14px; }
      .doc-footer-meta > div { color: var(--ink); font-size: 10.5px; letter-spacing: 0.05em; padding: 1px 0; }
      .doc-footer-meta .doc-footer-p { color: var(--muted); letter-spacing: 0.22em; text-transform: uppercase; }
      .doc-footer-page { margin-top: 3px; color: var(--accent-2) !important; letter-spacing: 0.28em !important; text-transform: uppercase; font-weight: 800; }

      @media (max-width: 900px) {
        .doc-footer { grid-template-columns: 1fr 1fr; }
        .doc-footer-col:nth-child(2) { border-left: 0; padding-left: 0; }
        .doc-footer-meta { text-align: left; border-left: 0; padding-left: 0; grid-column: 1 / -1; }
      }

      /* ---------- Header + tables responsive ---------- */
      @media (max-width: 900px) {
        .doc-header-grid { grid-template-columns: 1fr auto; }
        .doc-brand { grid-row: 1; grid-column: 1; }
        .doc-title-block { grid-row: 2; grid-column: 1 / -1; }
        .doc-header-meta { grid-row: 3; grid-column: 1 / -1; grid-auto-flow: row; }
        .doc-status-pill { grid-row: 1; grid-column: 2; align-self: start; }
        .doc-title { white-space: normal; }
      }
      @media (max-width: 620px) {
        .doc-header-grid { grid-template-columns: 1fr; grid-template-rows: auto auto auto auto; }
        .doc-brand { grid-row: 1; grid-column: 1; }
        .doc-title-block { grid-row: 2; grid-column: 1; }
        .doc-header-meta { grid-row: 3; grid-column: 1; }
        .doc-status-pill { grid-row: 4; grid-column: 1; justify-self: start; max-width: 100%; padding: 8px 12px; }
        .doc-status-primary { font-size: 11px; letter-spacing: 0.16em; }
        .doc-brand-logo { height: 52px; max-width: 200px; }
        .doc-table-two-col tr { display: grid; grid-template-columns: 40% 1fr; }
        .doc-table-two-col tr th:nth-child(3), .doc-table-two-col tr td:nth-child(4) { border-left: 0; grid-column: 1 / -1; }
        .doc-table-two-col tr th, .doc-table-two-col tr td { display: block; }
      }
      @media (max-width: 480px) {
        .doc-spec-row { grid-template-columns: 24px 1fr; }
        .doc-spec-value { grid-column: 1 / -1; margin-top: 4px; }
        .doc-total-time { grid-template-columns: 1fr; text-align: left; }
        .doc-total-icn { display: none; }
      }

      /* ---------- Print / A4 PDF (dark theme preserved) ---------- */
      @page { size: A4; margin: 12mm; }
      @media print {
        html, body { background: var(--paper) !important; }
        .no-print { display: none !important; }
        .print-only { display: block !important; }
        .idea-page-bg { background: var(--paper); }
        .a4-sheet { width: auto; min-height: 0; margin: 0; padding: 0; box-shadow: none; border: 0; background: var(--paper) !important; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .doc-section, .doc-media-frame, .doc-invoice-preview { break-inside: avoid; page-break-inside: avoid; }
        .print-pagenum {
          position: fixed; top: 0; right: 0;
          font-size: 9px; letter-spacing: 0.24em; text-transform: uppercase; color: var(--muted);
        }
      }
    `}</style>
  );
}
